"use client";

import { c as _c } from "react/compiler-runtime";
import { IconArrowTrendDownOutline18, IconArrowTrendUpOutline18, IconArrowUpRightOutline18, IconCopyOutline18 } from "nucleo-ui-outline-18";
import { Area, AreaChart } from "@/components/charts";
import { TokenWithChainIcon } from "@/components/ui/token-with-chain-icon";
import { useTokenPriceHistory } from "@/hooks/useTokenPriceHistory";
import type { SimBalanceItem } from "@/hooks/useSimTokenInfo";
import { isNativeTokenAddress } from "@/lib/tokens/nativeToken";
import { formatAddress } from "@/lib/utils";
import { showToast } from "@/lib/toast";
import { PixelLoader } from "@/components/ui/pixel-loader";
interface WalletTokenChainInfo {
  name?: string;
  iconUrl?: string | null;
  explorers?: Array<{
    url: string;
  }>;
}
interface TokenHoverTooltipContentProps {
  balance: SimBalanceItem;
  chainInfo?: WalletTokenChainInfo;
  open: boolean;
}
function formatUsdPrice(value: number | undefined | null): string {
  if (typeof value !== "number" || Number.isNaN(value) || value <= 0) {
    return "-";
  }
  if (value >= 1000) return `$${value.toLocaleString(undefined, {
    maximumFractionDigits: 2
  })}`;
  if (value >= 1) return `$${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4
  })}`;
  if (value >= 0.01) return `$${value.toLocaleString(undefined, {
    minimumFractionDigits: 4,
    maximumFractionDigits: 6
  })}`;
  return `$${value.toLocaleString(undefined, {
    minimumFractionDigits: 6,
    maximumFractionDigits: 8
  })}`;
}
function formatPoolSize(value: number | undefined | null): string {
  if (typeof value !== "number" || Number.isNaN(value) || value <= 0) {
    return "-";
  }
  if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(2)}B`;
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
}
export function TokenHoverTooltipContent(t0) {
  const $ = _c(74);
  const {
    balance,
    chainInfo,
    open
  } = t0;
  let t1;
  if ($[0] !== balance.address || $[1] !== balance.chain_id || $[2] !== open) {
    t1 = {
      tokenAddress: balance.address,
      chainId: balance.chain_id,
      enabled: open
    };
    $[0] = balance.address;
    $[1] = balance.chain_id;
    $[2] = open;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    data: priceHistory,
    isLoading: isPriceHistoryLoading
  } = useTokenPriceHistory(t1);
  let t2;
  if ($[4] !== priceHistory?.points) {
    t2 = priceHistory?.points ?? [];
    $[4] = priceHistory?.points;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== balance.address || $[7] !== balance.chain || $[8] !== balance.chain_id || $[9] !== balance.low_liquidity || $[10] !== balance.name || $[11] !== balance.pool_size || $[12] !== balance.price_usd || $[13] !== balance.symbol || $[14] !== balance.token_metadata?.logo || $[15] !== chainInfo || $[16] !== isPriceHistoryLoading || $[17] !== t2) {
    const chartData = t2.map(_temp);
    const firstPrice = chartData[0]?.price;
    const lastPrice = chartData[chartData.length - 1]?.price;
    const priceChangePct = typeof firstPrice === "number" && typeof lastPrice === "number" && firstPrice > 0 ? (lastPrice - firstPrice) / firstPrice * 100 : null;
    const isPositiveTrend = (priceChangePct ?? 0) >= 0;
    const trendColor = priceChangePct == null ? "#2450FF" : isPositiveTrend ? "#16a34a" : "#dc2626";
    let t4;
    if ($[19] !== balance.address) {
      t4 = isNativeTokenAddress(balance.address);
      $[19] = balance.address;
      $[20] = t4;
    } else {
      t4 = $[20];
    }
    const isNative = t4;
    const explorerUrl = !isNative && chainInfo?.explorers?.[0] ? `${chainInfo.explorers[0].url}/token/${balance.address}` : null;
    const hasHistory = chartData.length > 1;
    let t5;
    if ($[21] !== balance.address) {
      t5 = async () => {
        try {
          await navigator.clipboard.writeText(balance.address);
          showToast.success("Token address copied");
        } catch {
          showToast.error("Failed to copy token address");
        }
      };
      $[21] = balance.address;
      $[22] = t5;
    } else {
      t5 = $[22];
    }
    const handleCopyAddress = t5;
    const t6 = balance.token_metadata?.logo;
    const t7 = chainInfo?.iconUrl;
    const t8 = chainInfo?.name;
    let t9;
    if ($[23] !== balance.address || $[24] !== balance.chain_id || $[25] !== balance.symbol || $[26] !== t6 || $[27] !== t7 || $[28] !== t8) {
      t9 = <TokenWithChainIcon logoUrl={t6} chainId={balance.chain_id} tokenAddress={balance.address} symbol={balance.symbol} tokenSize={36} chainIconUrl={t7} chainName={t8} chainIconSize={16} />;
      $[23] = balance.address;
      $[24] = balance.chain_id;
      $[25] = balance.symbol;
      $[26] = t6;
      $[27] = t7;
      $[28] = t8;
      $[29] = t9;
    } else {
      t9 = $[29];
    }
    let t10;
    if ($[30] !== balance.symbol) {
      t10 = <span className="body-sm-semibold">{balance.symbol}</span>;
      $[30] = balance.symbol;
      $[31] = t10;
    } else {
      t10 = $[31];
    }
    let t11;
    if ($[32] !== chainInfo) {
      t11 = chainInfo?.name ? <span className="body-sm">on {chainInfo.name}</span> : null;
      $[32] = chainInfo;
      $[33] = t11;
    } else {
      t11 = $[33];
    }
    let t12;
    if ($[34] !== t10 || $[35] !== t11) {
      t12 = <div className="flex flex-wrap items-center gap-x-2 gap-y-1">{t10}{t11}</div>;
      $[34] = t10;
      $[35] = t11;
      $[36] = t12;
    } else {
      t12 = $[36];
    }
    let t13;
    if ($[37] !== balance.name || $[38] !== balance.symbol) {
      t13 = balance.name && balance.name !== balance.symbol ? <p className="label-xs mt-0.5">{balance.name}</p> : null;
      $[37] = balance.name;
      $[38] = balance.symbol;
      $[39] = t13;
    } else {
      t13 = $[39];
    }
    let t14;
    if ($[40] !== t12 || $[41] !== t13) {
      t14 = <div className="min-w-0">{t12}{t13}</div>;
      $[40] = t12;
      $[41] = t13;
      $[42] = t14;
    } else {
      t14 = $[42];
    }
    let t15;
    if ($[43] !== t14 || $[44] !== t9) {
      t15 = <div className="flex min-w-0 items-start gap-3">{t9}{t14}</div>;
      $[43] = t14;
      $[44] = t9;
      $[45] = t15;
    } else {
      t15 = $[45];
    }
    let t16;
    if ($[46] !== balance.price_usd) {
      t16 = formatUsdPrice(balance.price_usd);
      $[46] = balance.price_usd;
      $[47] = t16;
    } else {
      t16 = $[47];
    }
    let t17;
    if ($[48] !== t16) {
      t17 = <div className="body-sm-semibold">{t16}</div>;
      $[48] = t16;
      $[49] = t17;
    } else {
      t17 = $[49];
    }
    let t18;
    if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = <div className="label-2xs">spot price</div>;
      $[50] = t18;
    } else {
      t18 = $[50];
    }
    let t19;
    if ($[51] !== t17) {
      t19 = <div className="text-right">{t17}{t18}</div>;
      $[51] = t17;
      $[52] = t19;
    } else {
      t19 = $[52];
    }
    let t20;
    if ($[53] !== t15 || $[54] !== t19) {
      t20 = <div className="flex items-start justify-between gap-3">{t15}{t19}</div>;
      $[53] = t15;
      $[54] = t19;
      $[55] = t20;
    } else {
      t20 = $[55];
    }
    let t21;
    if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
      t21 = <div className="micro-label">Liquidity</div>;
      $[56] = t21;
    } else {
      t21 = $[56];
    }
    let t22;
    if ($[57] !== balance.pool_size) {
      t22 = formatPoolSize(balance.pool_size);
      $[57] = balance.pool_size;
      $[58] = t22;
    } else {
      t22 = $[58];
    }
    let t23;
    if ($[59] !== t22) {
      t23 = <div>{t21}<div className="body-sm-medium mt-1">{t22}</div></div>;
      $[59] = t22;
      $[60] = t23;
    } else {
      t23 = $[60];
    }
    let t24;
    if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
      t24 = <div className="micro-label">Quote Quality</div>;
      $[61] = t24;
    } else {
      t24 = $[61];
    }
    const t25 = balance.low_liquidity ? "Low liquidity" : "Healthy";
    let t26;
    if ($[62] !== t25) {
      t26 = <div>{t24}<div className="body-sm-medium mt-1">{t25}</div></div>;
      $[62] = t25;
      $[63] = t26;
    } else {
      t26 = $[63];
    }
    let t27;
    if ($[64] !== t23 || $[65] !== t26) {
      t27 = <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3">{t23}{t26}</div>;
      $[64] = t23;
      $[65] = t26;
      $[66] = t27;
    } else {
      t27 = $[66];
    }
    let t28;
    if ($[67] !== balance.address || $[68] !== balance.chain || $[69] !== chainInfo?.name || $[70] !== explorerUrl || $[71] !== handleCopyAddress || $[72] !== isNative) {
      t28 = !isNative ? <div className="border-ds-border-default mt-3 flex items-center justify-between gap-3 border-t pt-3"><div className="min-w-0"><div className="micro-label">Contract</div><div className="body-sm-medium mt-1 truncate font-mono">{formatAddress(balance.address, 4, 4)}</div></div><div className="flex items-center gap-1"><button type="button" onClick={handleCopyAddress} className="text-ds-text-muted hover:bg-ds-surface-hover hover:text-ds-text-primary rounded-md p-1.5 transition-colors" aria-label="Copy token address"><IconCopyOutline18 className="h-3.5 w-3.5" /></button>{explorerUrl ? <a href={explorerUrl} target="_blank" rel="noopener noreferrer" className="text-ds-text-muted hover:bg-ds-surface-hover hover:text-ds-text-primary rounded-md p-1.5 transition-colors" aria-label="Open token in explorer"><IconArrowUpRightOutline18 className="h-3.5 w-3.5" /></a> : null}</div></div> : <div className="border-ds-border-default mt-3 border-t pt-3"><div className="micro-label">Contract</div><div className="body-sm mt-1">Native asset on {chainInfo?.name || balance.chain}</div></div>;
      $[67] = balance.address;
      $[68] = balance.chain;
      $[69] = chainInfo?.name;
      $[70] = explorerUrl;
      $[71] = handleCopyAddress;
      $[72] = isNative;
      $[73] = t28;
    } else {
      t28 = $[73];
    }
    t3 = <div className="w-[min(20rem,calc(100vw-2rem))] p-3">{t20}{t27}{t28}{hasHistory ? <div className="border-ds-border-default mt-3 border-t pt-3"><div className="flex items-center justify-between gap-2"><div className="micro-label">30D Price History</div>{priceChangePct != null ? <div className={`label-2xs inline-flex items-center gap-1 ${isPositiveTrend ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>{isPositiveTrend ? <IconArrowTrendUpOutline18 className="h-3 w-3" /> : <IconArrowTrendDownOutline18 className="h-3 w-3" />}{Math.abs(priceChangePct).toFixed(2)}%</div> : null}</div>{isPriceHistoryLoading ? <div className="text-ds-text-muted flex h-16 items-center justify-center"><PixelLoader size="sm" /></div> : <><AreaChart data={chartData as Record<string, unknown>[]} xDataKey="date" className="pointer-events-none mt-2 h-16 w-full" aspectRatio="4 / 1" animationDuration={450} margin={{
            top: 6,
            right: 2,
            bottom: 4,
            left: 2
          }}><Area dataKey="price" fill={trendColor} stroke={trendColor} fillOpacity={0.18} gradientToOpacity={0} strokeWidth={2} fadeEdges={true} /></AreaChart><div className="label-2xs mt-1 flex items-center justify-between"><span>{formatUsdPrice(firstPrice)}</span><span>{formatUsdPrice(lastPrice)}</span></div></>}</div> : null}</div>;
    $[6] = balance.address;
    $[7] = balance.chain;
    $[8] = balance.chain_id;
    $[9] = balance.low_liquidity;
    $[10] = balance.name;
    $[11] = balance.pool_size;
    $[12] = balance.price_usd;
    $[13] = balance.symbol;
    $[14] = balance.token_metadata?.logo;
    $[15] = chainInfo;
    $[16] = isPriceHistoryLoading;
    $[17] = t2;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  return t3;
}
function _temp(point) {
  return {
    date: point.date,
    price: point.priceUsd
  };
}